import React, { useState, useCallback } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress, Link, ThemeProvider, createTheme } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from './AuthProvider';
import axios from 'axios';
import { passkeysApi } from '../api/services';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

// Force dark theme for login page — it has its own dark gradient background
// and must remain dark regardless of the user's theme preference.
const darkLoginTheme = createTheme({ palette: { mode: 'dark' } });

// Default background: dark geometric gradient that conveys structure, strategy, and precision.
// Tenants can override this via their branding settings (loginBackgroundUrl).
const defaultBackground = `
  radial-gradient(ellipse at 20% 50%, rgba(124, 58, 237, 0.15) 0%, transparent 50%),
  radial-gradient(ellipse at 80% 20%, rgba(37, 99, 235, 0.12) 0%, transparent 50%),
  radial-gradient(ellipse at 60% 80%, rgba(13, 148, 136, 0.08) 0%, transparent 50%),
  linear-gradient(180deg, #060912 0%, #0A0E17 50%, #0D1220 100%)
`;

// Subtle grid pattern overlay
const gridOverlay = `
  linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
  linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
`;

interface SsoCheckResult {
  ssoEnabled: boolean;
  provider?: string;
  displayName?: string;
  loginUrl?: string;
  enforceSso?: boolean;
  error?: string;
}

export default function LoginPage() {
  const { login, verifyMfa, requiresMfa } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // SSO state
  const [ssoChecked, setSsoChecked] = useState(false);
  const [ssoChecking, setSsoChecking] = useState(false);
  const [ssoResult, setSsoResult] = useState<SsoCheckResult | null>(null);

  // Passkey state
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  // Magic link state
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  const checkSso = useCallback(async (emailValue: string) => {
    if (!emailValue || !emailValue.includes('@')) return;
    setSsoChecking(true);
    try {
      const { data } = await axios.get<SsoCheckResult>(
        `${API_URL}/sso/login?email=${encodeURIComponent(emailValue)}`
      );
      setSsoResult(data);
      setSsoChecked(true);
    } catch {
      // SSO check failed — fall back to password login
      setSsoResult(null);
      setSsoChecked(true);
    } finally {
      setSsoChecking(false);
    }
  }, []);

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    // If SSO hasn't been checked yet, check it
    if (!ssoChecked) {
      await checkSso(email);
      return;
    }

    // If SSO is enforced, redirect
    if (ssoResult?.ssoEnabled && ssoResult.enforceSso && ssoResult.loginUrl) {
      window.location.href = ssoResult.loginUrl;
      return;
    }

    // Otherwise do normal password login
    if (!password) return;
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.ssoEnforced) {
        setError('Your organisation requires SSO login. Use the SSO button below.');
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.ssoEnforced) {
        setError('Your organisation requires SSO login. Use the SSO button below.');
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaCode);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid MFA code.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    // Reset SSO state when email changes
    if (ssoChecked) {
      setSsoChecked(false);
      setSsoResult(null);
    }
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetEmail = magicLinkEmail || email;
    if (!targetEmail) return;
    setError('');
    setMagicLinkLoading(true);
    try {
      await axios.post(`${API_URL}/auth/magic-link`, { email: targetEmail });
      setMagicLinkSent(true);
    } catch {
      // The API always returns 200 to prevent enumeration,
      // but handle network errors gracefully
      setError('Unable to send magic link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      // Get assertion options from the server
      const options = await passkeysApi.loginOptions(email || undefined);
      // Preserve the base64url challenge as the cache key (server stores it this way)
      const challengeKey = options.challenge;

      // Prompt the browser's WebAuthn UI
      const assertion = await startAuthentication({ optionsJSON: options });

      // Send to server for verification
      const result = await passkeysApi.login({ response: assertion, challengeKey });

      // Store tokens and redirect (same as password login)
      const authData = {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        user: {
          id: result.userId,
          email: result.displayName,
          displayName: result.displayName,
          role: result.role,
          tenantId: result.tenantId,
        },
      };
      sessionStorage.setItem('auth', JSON.stringify(authData));
      window.location.href = '/';
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled.');
      } else {
        setError(err.response?.data?.error || 'Passkey authentication failed. Please try again.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const showPasswordField = ssoChecked && !(ssoResult?.ssoEnabled && ssoResult.enforceSso);
  const showSsoButton = ssoChecked && ssoResult?.ssoEnabled;

  return (
    <ThemeProvider theme={darkLoginTheme}>
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        background: defaultBackground,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: gridOverlay,
          backgroundSize: '60px 60px',
          opacity: 1,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Floating accent orb — top right */}
      <Box sx={{
        position: 'absolute', top: -120, right: -80,
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)',
        filter: 'blur(40px)', pointerEvents: 'none',
      }} />

      {/* Floating accent orb — bottom left */}
      <Box sx={{
        position: 'absolute', bottom: -100, left: -60,
        width: 350, height: 350, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)',
        filter: 'blur(40px)', pointerEvents: 'none',
      }} />

      {/* Login card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 420,
          width: '100%',
          mx: 2,
          p: 5,
          borderRadius: 3,
          background: 'rgba(18, 24, 41, 0.85)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 80px rgba(124,58,237,0.05)',
        }}
      >
        {/* Logo & branding */}
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 700, color: '#fff',
          }}>
            C
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{
            background: 'linear-gradient(135deg, #E8ECF4 0%, #8892A8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
          }}>
            Cadence
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={4} sx={{ fontSize: 13, letterSpacing: '0.02em' }}>
          Executive Orchestration Platform
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {!requiresMfa ? (
          <>
            <form onSubmit={ssoChecked ? handleLogin : handleEmailContinue}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={email}
                onChange={e => handleEmailChange(e.target.value)}
                onBlur={() => { if (email && !ssoChecked) checkSso(email); }}
                sx={{ mb: showPasswordField ? 2.5 : 3 }}
                autoFocus
                variant="outlined"
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.03)',
                    color: '#E8ECF4',
                    '& input': { color: '#E8ECF4' },
                  }
                }}
                InputLabelProps={{ sx: { color: 'rgba(136,146,168,0.8)' } }}
              />

              {ssoChecking && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <CircularProgress size={20} sx={{ color: 'rgba(136,146,168,0.6)' }} />
                </Box>
              )}

              {/* SSO button — shown when SSO is available for this domain */}
              {showSsoButton && (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  href={ssoResult?.loginUrl || '#'}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: 15,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)' },
                    boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                    textTransform: 'none',
                  }}
                >
                  {ssoResult?.displayName || `Sign in with ${ssoResult?.provider || 'SSO'}`}
                </Button>
              )}

              {/* Enforce SSO message */}
              {ssoResult?.ssoEnabled && ssoResult.enforceSso && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: 12, textAlign: 'center' }}>
                  Your organisation requires SSO authentication.
                </Typography>
              )}

              {/* Password field — hidden when SSO is enforced */}
              {showPasswordField && (
                <>
                  {showSsoButton && (
                    <Box display="flex" alignItems="center" my={2} gap={2}>
                      <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.08)" />
                      <Typography variant="body2" sx={{ fontSize: 12, color: 'rgba(136,146,168,0.6)' }}>or use password</Typography>
                      <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.08)" />
                    </Box>
                  )}
                  <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    sx={{ mb: 1 }}
                    variant="outlined"
                    InputProps={{
                      sx: {
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.03)',
                        color: '#E8ECF4',
                        '& input': { color: '#E8ECF4' },
                      }
                    }}
                    InputLabelProps={{ sx: { color: 'rgba(136,146,168,0.8)' } }}
                  />
                  <Box sx={{ textAlign: 'right', mb: 2.5 }}>
                    <Link href="/reset-password" underline="hover" sx={{ color: 'rgba(136,146,168,0.8)', fontSize: 12, '&:hover': { color: '#7C3AED' } }}>
                      Forgot password?
                    </Link>
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: 15,
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)' },
                      boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
                    }}
                  >
                    {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
                  </Button>
                </>
              )}

              {/* Continue button — shown before SSO check */}
              {!ssoChecked && !ssoChecking && (
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={!email}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: 15,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)' },
                    boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
                  }}
                >
                  Continue
                </Button>
              )}
            </form>

            {/* OAuth divider — only show when password field is visible */}
            {showPasswordField && (
              <>
                <Box display="flex" alignItems="center" my={3} gap={2}>
                  <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.08)" />
                  <Typography variant="body2" sx={{ fontSize: 12, color: 'rgba(136,146,168,0.6)' }}>or</Typography>
                  <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.08)" />
                </Box>

                {/* OAuth buttons */}
                <Button
                  fullWidth
                  variant="outlined"
                  href={`${process.env.REACT_APP_API_URL || 'https://api.stratiumhq.com/api'}/auth/login/microsoft`}
                  sx={{
                    mb: 1.5, py: 1.2, borderRadius: 2, fontSize: 14, fontWeight: 500,
                    borderColor: 'rgba(255,255,255,0.12)', color: '#E8ECF4',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.04)' },
                    textTransform: 'none',
                  }}
                >
                  Sign in with Microsoft 365
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  href={`${process.env.REACT_APP_API_URL || 'https://api.stratiumhq.com/api'}/auth/login/google`}
                  sx={{
                    py: 1.2, borderRadius: 2, fontSize: 14, fontWeight: 500,
                    borderColor: 'rgba(255,255,255,0.12)', color: '#E8ECF4',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.04)' },
                    textTransform: 'none',
                  }}
                >
                  Sign in with Google
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handlePasskeyLogin}
                  disabled={passkeyLoading}
                  startIcon={passkeyLoading ? <CircularProgress size={16} sx={{ color: '#B794F4' }} /> : <FingerprintIcon />}
                  sx={{
                    mt: 1.5, py: 1.2, borderRadius: 2, fontSize: 14, fontWeight: 500,
                    borderColor: 'rgba(124,58,237,0.3)', color: '#B794F4',
                    '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' },
                    textTransform: 'none',
                  }}
                >
                  Sign in with passkey
                </Button>

                {/* Magic link option */}
                {!showMagicLink && (
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => { setShowMagicLink(true); setMagicLinkEmail(email); }}
                    sx={{
                      mt: 1.5, py: 1, fontSize: 13, fontWeight: 500,
                      color: 'rgba(136,146,168,0.8)',
                      '&:hover': { color: '#7C3AED', background: 'rgba(124,58,237,0.06)' },
                      textTransform: 'none',
                    }}
                  >
                    Sign in with email link
                  </Button>
                )}

                {showMagicLink && !magicLinkSent && (
                  <Box sx={{ mt: 2 }}>
                    <Box display="flex" alignItems="center" mb={1.5} gap={2}>
                      <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.08)" />
                      <Typography variant="body2" sx={{ fontSize: 12, color: 'rgba(136,146,168,0.6)' }}>email link</Typography>
                      <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.08)" />
                    </Box>
                    <form onSubmit={handleSendMagicLink}>
                      <TextField
                        label="Email for magic link"
                        type="email"
                        fullWidth
                        required
                        value={magicLinkEmail}
                        onChange={e => setMagicLinkEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        variant="outlined"
                        InputProps={{
                          sx: {
                            borderRadius: 2,
                            background: 'rgba(255,255,255,0.03)',
                            color: '#E8ECF4',
                            '& input': { color: '#E8ECF4' },
                          }
                        }}
                        InputLabelProps={{ sx: { color: 'rgba(136,146,168,0.8)' } }}
                      />
                      <Button
                        type="submit"
                        variant="outlined"
                        fullWidth
                        disabled={magicLinkLoading || !magicLinkEmail}
                        sx={{
                          py: 1.2, borderRadius: 2, fontSize: 14, fontWeight: 500,
                          borderColor: 'rgba(124,58,237,0.4)', color: '#B794F4',
                          '&:hover': { borderColor: '#7C3AED', background: 'rgba(124,58,237,0.08)' },
                          textTransform: 'none',
                        }}
                      >
                        {magicLinkLoading ? <CircularProgress size={20} sx={{ color: '#B794F4' }} /> : 'Send magic link'}
                      </Button>
                    </form>
                  </Box>
                )}

                {magicLinkSent && (
                  <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                    Check your email for a login link. It expires in 15 minutes.
                  </Alert>
                )}
              </>
            )}
          </>
        ) : (
          <form onSubmit={handleMfa}>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Enter the 6-digit code from your authenticator app.
            </Typography>
            <TextField
              label="MFA Code"
              fullWidth
              required
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value)}
              inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
              sx={{ mb: 3.5 }}
              autoFocus
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.03)',
                  letterSpacing: '0.3em',
                  textAlign: 'center',
                  color: '#E8ECF4',
                  '& input': { color: '#E8ECF4' },
                }
              }}
              InputLabelProps={{ sx: { color: 'rgba(136,146,168,0.8)' } }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: 15,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)' },
                boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify'}
            </Button>
          </form>
        )}

        {/* Footer links */}
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            Don't have an account?{' '}
            <Link href="/signup" underline="hover" sx={{ color: '#7C3AED', fontWeight: 600 }}>
              Start free trial
            </Link>
          </Typography>
        </Box>

        {/* Stratium footer */}
        <Box mt={3} textAlign="center">
          <Typography variant="body2" sx={{ fontSize: 11, color: 'rgba(136,146,168,0.5)' }}>
            Powered by Stratium
          </Typography>
        </Box>
      </Box>
    </Box>
    </ThemeProvider>
  );
}
